import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #recommendationsController = null;

  #loadedRecommendationsUrl = '';

  /** @type {Promise<unknown> | null} */
  #quantitySelectorPromise = null;

  /** @type {HTMLElement | null} */
  #cartTrigger = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.#cartTrigger = this.querySelector(':scope > button');
    this.#cartTrigger?.addEventListener('pointerenter', this.#handleCartIntent, { passive: true });
    this.#cartTrigger?.addEventListener('pointerdown', this.#handleCartIntent, { passive: true });
    this.#cartTrigger?.addEventListener('focusin', this.#handleCartIntent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#cartTrigger?.removeEventListener('pointerenter', this.#handleCartIntent);
    this.#cartTrigger?.removeEventListener('pointerdown', this.#handleCartIntent);
    this.#cartTrigger?.removeEventListener('focusin', this.#handleCartIntent);
    this.#cartTrigger = null;
    this.#recommendationsController?.abort();
  }

  #handleCartIntent = () => {
    void this.#ensureQuantitySelector();
  };

  #handleCartAdd = () => {
    void this.#ensureQuantitySelector();

    requestAnimationFrame(() => {
      const shouldOpen = this.hasAttribute('auto-open');
      if (shouldOpen) {
        this.showDialog();
      }

      if (shouldOpen || this.refs.dialog?.open) {
        this.#loadRecommendations();
      }
    });
  };

  async #ensureQuantitySelector() {
    if (customElements.get('quantity-selector-component')) return;

    this.#quantitySelectorPromise ??= import('@theme/quantity-selector');

    try {
      await this.#quantitySelectorPromise;
    } catch (error) {
      this.#quantitySelectorPromise = null;
      console.error('Unable to load cart quantity controls.', error);
    }
  }

  async #loadRecommendations() {
    const container = this.querySelector('[data-cart-drawer-recommendations]');
    const list = container?.querySelector('[data-cart-drawer-recommendations-list]');
    const url = container?.getAttribute('data-recommendations-url');

    if (
      !(container instanceof HTMLElement) ||
      !(list instanceof HTMLElement) ||
      !url ||
      (url === this.#loadedRecommendationsUrl && list.children.length > 0)
    ) {
      return;
    }

    container.hidden = true;
    this.#recommendationsController?.abort();
    const controller = new AbortController();
    this.#recommendationsController = controller;
    list.setAttribute('aria-busy', 'true');

    try {
      let source = await this.#fetchRecommendationList(url, controller.signal);
      if (!source || source.children.length === 0) {
        const shopifyRoot = window.Shopify?.routes?.root || '/';
        const fallbackUrl = new URL(shopifyRoot, window.location.origin);
        fallbackUrl.searchParams.set('section_id', 'cart-drawer-recommendations');
        source = await this.#fetchRecommendationList(fallbackUrl.toString(), controller.signal);
      }

      if (!source || source.children.length === 0) {
        throw new Error('No available cart recommendations were returned.');
      }
      if (controller.signal.aborted) return;

      await import('@theme/oba-add-to-cart');

      if (controller.signal.aborted) return;

      list.replaceChildren(...source.children);
      container.hidden = list.children.length === 0;
      this.#loadedRecommendationsUrl = url;
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        list.replaceChildren();
        container.hidden = true;
        console.warn(error);
      }
    } finally {
      if (this.#recommendationsController === controller) {
        this.#recommendationsController = null;
        list.setAttribute('aria-busy', 'false');
      }
    }
  }

  /**
   * Requests a recommendation section and extracts its product list.
   *
   * @param {string} url
   * @param {AbortSignal} signal
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchRecommendationList(url, signal) {
    const response = await fetch(url, {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) return null;

    const html = await response.text();
    const source = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-cart-drawer-recommendations-list]');

    return source instanceof HTMLElement ? source : null;
  }

  open() {
    this.showDialog();
    void this.#ensureQuantitySelector();
    this.#loadRecommendations();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
